import React, { createContext, useContext, useMemo, useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { CssBaseline } from "@mui/material";
import { colors } from "../../styles";
import { SiteConfiguration } from "../../generated/graphql";

type ThemeContextType = {
  selectedTheme: SiteConfiguration;
  setSelectedTheme: (theme: SiteConfiguration) => void;
};

const ThemeContext = createContext<ThemeContextType | null>(null);

export const ThemeProviderWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [selectedTheme, setSelectedTheme] = useState<SiteConfiguration>({
    primaryColor: colors.orange,
    secondaryColor: colors.white,
  });

  const theme = useMemo(() => createTheme({
    palette: {
      primary: {
          main: selectedTheme.primaryColor ?? colors.orange,
        }
      },
      typography: {
        fontFamily: 'Poppins',
        allVariants: {
          color: selectedTheme.primaryColor ?? colors.orange,
        },
        body1: {
          '&.MuiFormControlLabel-label': {
            color: selectedTheme.secondaryColor ?? colors.orange,
          },
        }
      },
      components: {
        MuiInput: {
          styleOverrides: {
            root: {
              color: selectedTheme.primaryColor ?? colors.orange,
            }
          }
        },
        MuiTextField: {
          defaultProps: {
            InputProps: {
              sx: {
                color: `${selectedTheme.primaryColor ?? colors.orange} !important`,
              }
            }
          },
          styleOverrides: {
            root: {
              "& .MuiFormLabel-root": {
                color: selectedTheme.primaryColor ?? colors.orange,
              },
              "& .MuiFormLabel-root.Mui-focused": {
                color: selectedTheme.primaryColor ?? colors.orange,
              },
              '& .MuiOutlinedInput-root': {
                '&:hover fieldset': {
                  borderColor: selectedTheme.primaryColor ?? colors.orange,
                  '& legend': {
                    color: selectedTheme.primaryColor ?? colors.orange
                  }
                },
                '&.Mui-focused fieldset': {
                  borderColor: selectedTheme.primaryColor ?? colors.orange,
                  '& legend': {
                    color: selectedTheme.primaryColor ?? colors.orange
                  }
                },
              }
            }
          }
        },
        MuiChip: {
          styleOverrides: {
            root: {
              borderColor: selectedTheme.primaryColor ?? colors.orange,
              // backgroundColor: selectedTheme.primaryColor ?? colors.orange,
              color: selectedTheme.secondaryColor ?? colors.orange,
              // fontSize: '2.5rem',
              '&:focus': {
                borderColor: selectedTheme.primaryColor ?? colors.orange,
              },
            },
          }
        },
        MuiButton: {
          styleOverrides: {
            root: {
              borderColor: selectedTheme.primaryColor ?? colors.orange,
              color: selectedTheme.secondaryColor ?? colors.orange,
              fontSize: '2.5rem',
              '&:focus': {
                borderColor: selectedTheme.primaryColor ?? colors.orange,
              },
            },
            contained: {
              backgroundColor: selectedTheme.primaryColor ?? colors.orange,
              borderColor: selectedTheme.primaryColor ?? colors.orange,
              fontSize: '2rem',
              color: colors.white,
              ":hover": {
                backgroundColor: selectedTheme.primaryColor ?? colors.orange,
                color: colors.white,
              }
            },
            // focusVisible: {
            //   borderColor: selectedTheme.primaryColor ?? colors.orange,
            // }
          }
        },
        MuiCheckbox: {
          styleOverrides: {
            root: {
              "& .MuiSvgIcon-root": {
                fill: selectedTheme.primaryColor ?? colors.orange
              }
            }
          }
        },
        MuiFormControlLabel: {
          styleOverrides: {
            root: {
              color: selectedTheme.secondaryColor ?? colors.orange,
            }
          }
        }
      }
  }), [selectedTheme]);

  return (
    <ThemeContext.Provider value={{
      selectedTheme: selectedTheme,
      setSelectedTheme: (theme: SiteConfiguration) => {
        setSelectedTheme(theme);
      }
    }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

// Custom hook (optional, in case you need it)
export const useThemeContext = () => useContext(ThemeContext);

export const useThemeSelector = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error("useSiteSelector must be used within a SiteSelectorProvider");
  }
  return context;
};